/**
 * Content Domain — Type Definitions
 *
 * All types related to questions, modules, taxonomy, and content structure.
 * Extracted from src/types.ts in R30 (DDD Fase 3 — types split).
 *
 * Includes: ModuleId, ModuleData, QuestionType, QuestionCore, Question,
 * QuestionFeedback, QuestionData, Clock/Money configs, chart/schedule types.
 */

import type { VisualAid } from '../visuals';

// ============================================================
// Module identification
// ============================================================

export type ModuleId = 'Metriek' | 'Getalbegrip' | 'Basisrekenen' | 'Tijd' | 'Geld' | 'SpellingWW' | 'SpellingRegel' | 'Taalbegrip' | 'Studievaardig' | 'Topo';
export interface ModuleData {
  id: ModuleId;
  title: string;
  description: string;
  icon: string;
  color: string;
  /**
   * True when the module's questions contain numeric content (operands,
   * measurements, monetary values, time durations). Drives `NUMERIC_CONTENT_MODULE_IDS`
   * which the governance pipeline consults to decide whether to run
   * number-pattern-collapse analysis. Omit / false for language and
   * topography modules. SSOT — do not duplicate this set elsewhere.
   */
  hasNumericContent?: boolean;
}

// ============================================================
// Pedagogical Contract Types (PEDAGOGICAL_CONTRACT_SPEC §3)
// ============================================================

/** Depth of Knowledge level (Webb's DOK, adapted for Dutch primary education) */
export type DOKLevel = 1 | 2 | 3 | 4;

/** Misconception tag for distractor analysis */
export interface MisconceptionTag {
  /** Machine-readable misconception identifier (e.g., 'place-value-swap', 'dt-overgeneralize') */
  id: string;
  /** Human-readable label (Dutch) */
  label: string;
  /** Which option index this misconception applies to (0-based) */
  optionIndex: number;
}

/** Pedagogical contract attached to a question */
export interface PedagogyContract {
  /** Depth of Knowledge level for this question */
  dok?: DOKLevel;
  /** Misconception tags for distractors */
  misconceptionTags?: MisconceptionTag[];
  /** Whether structured data exists (vs regex-parsed from question text) */
  hasStructuredData?: boolean;
  /** Question mode — instruction mode allows visuals to show the answer */
  mode?: 'practice' | 'instruction' | 'assessment';
  /**
   * Visual indicator parity tolerance.
   * If a visual indicator value is within this fraction of the correct answer,
   * it's flagged as leaking the answer. Default: 0.05 (5%).
   */
  indicatorParityTolerance?: number;
  /**
   * Marker: `dok` was injected by seedConsolidator's heuristic auto-tagger
   * (estimateDok with confidence >= medium). Governance can surface this so
   * content authors can confirm or override the auto-filled value.
   */
  dokAutoFilled?: boolean;
  /**
   * Marker: `misconceptionTags` were stub-generated by seedConsolidator from
   * the misconception registry (typicalFamilies match) because the seed had
   * no explicit tags. Best-guess only — authors should review and override.
   */
  misconceptionsAutoFilled?: boolean;
}

// ============================================================
// Question Types
// ============================================================

export type QuestionType =
// CITO-eligible types
'multiple_choice'
// Practice-only interactive types (with dedicated renderers)
| 'fill_in' | 'drag_drop_fill' | 'matching' | 'ordering' | 'table_reading' | 'number_sequence' | 'ratio_table' | 'text_highlight'
// Visual Data types
| 'bar_chart' | 'line_chart' | 'pie_chart' | 'schedule_reading'
// Specialized interactive types
| 'cijferend_rekenen' | 'cijferend_delen'
// CANONICAL custom types (MeesterJay 4.1)
| 'clock' | 'clock_set' | 'money' | 'multiplication_grid' | 'true_false_series' | 'estimation_slider'
// NEW: Prioriteit 1 interactieve types (QUESTION_TYPES_AND_UPGRADE.md v4.0)
| 'categorize' | 'error_spotting'
// NEW: 4 new question types
| 'grid_shade' | 'step_solver' | 'sentence_parser' | 'comparison_judge'
// NEW: Verb conjugation decision-tree question type
| 'verb_conjugation_flow' | 'thermometer_slider'
// NEW: Sentence-level error picking (student picks which sentence has an error)
| 'sentence_error_judge'
// NEW: TOPO map question types
| 'map_locate_region'
/**
 * map_locate_point — Click on the map to mark a location.
 * mode 'free' (default): vrij klikken op kaart, validator gebruikt haversine + toleranceKm
 * mode 'snap-to-dot': klikken op één van candidateCityIds, validator vergelijkt cityId exact
 */ | 'map_locate_point'
/**
 * coordinate_plot — Click on a coordinate grid to place a point.
 * Replaces fill_in for coordinate questions to remove text-format fragility.
 */ | 'coordinate_plot'
/**
 * map_locate_line — Click on a river or waterway on the map.
 * Validator uses point-to-polyline haversine distance against the target
 * river polyline from atlases/nl/rivers.json. Multiple rivers are drawn
 * identically so the kid must identify which line belongs to the named river.
 */ | 'map_locate_line' | 'map_place_cities' | 'square_root_builder' | 'place_value_build' | 'bar_model_part' | 'strategy_solver' | 'fraction_equivalence_match' | 'fraction_strip_builder' | 'double_number_line' | 'comma_shift' | 'scientific_notation_builder'
/**
 * spelling_pick — SPR specialist grapheme-choice cloze.
 * A Dutch word is shown with a gap where the spelling-rule target letters
 * belong (before + [gap] + after); the learner taps the correct grapheme
 * tile (e.g. ei vs ij, au vs ou, d vs t) to complete it. Productive (the
 * rule must be applied/recalled) yet fast and playful (tap, not type).
 * Fills the gap between generic fill_in and full-word recognition for the
 * recall-heavy SPR families.
 */ | 'spelling_pick'
/**
 * verb_ending — SWW specialist werkwoord-uitgang cloze (dictation-grade).
 * The dictation sister of spelling_pick: a verb STEM is shown in a sentence
 * context with a highlighted gap immediately after the stem (before + stem +
 * [gap] + after); the learner taps the correct ENDING tile (e.g. ∅ / 't' /
 * 'dt' / 'te' / 'de'). This is "stam + keuze van de uitgang in context" —
 * the learner must APPLY the ending rule (produce the ending), not recognise
 * a fully conjugated word from MC options. Maps 1-to-1 onto the classic
 * -d/-t/-dt and kofschip dictation skill. Binary scoring per gap.
 */ | 'verb_ending'
/**
 * word_builder — SPR specialist full-word producer (dictee-waardig).
 * The full-word-production sister of spelling_pick: maximal production, ZERO
 * recognition. The learner builds the ENTIRE word from a shuffled letter bank
 * (tap-to-place), with NO answer options and NO grapheme choice — every
 * letter must be produced. A short `clue` says WHICH word to spell without
 * revealing its spelling. The most dictatish step for the recall-heavy SPR
 * weet-families (SPR-WEET-EI-IJ, SPR-WEET-D-T, SPR-WEET-DT). Binary scoring:
 * the built string must equal the target `word` (case-insensitive).
 */ | 'word_builder';

// ============================================================
// Clock Question Type Definitions (MeesterJay 4.1)
// ============================================================

export type ClockMode = 'read_analog_fill' // analoog → HH:MM invullen
| 'read_digital_fill' // digitaal → HH:MM invullen
| 'analog_to_digital_mc' // analoog → 4 digitale opties
| 'digital_to_analog_mc' // digitaal → 4 analoge opties
| 'set_analog'; // tekst → analoog instellen

export interface ClockConfig {
  mode: ClockMode;
  target: {
    hours: number;
    minutes: number;
    format: '24h';
  };
  analog?: {
    minuteStep?: 5 | 1;
    showNumbers?: boolean;
    showMinuteTicks?: boolean;
  };
  digital?: {

    // Future: format options
  };
  options?: Array<{
    id: string;
    label: string;
    value: {
      hours: number;
      minutes: number;
    };
  }>;
}
export interface ClockFeedback {
  correct: string;
  wrongByHour?: string;
  wrongByMinutes?: string;
  halfPastTip?: string;
  genericWrong: string;
}

// ============================================================
// Money Question Type Definitions (MeesterJay 4.1)
// ============================================================

export type MoneyMode = 'make_amount' // maak bedrag exact
| 'give_change'; // wisselgeld teruggeven

export interface MoneyDenom {
  valueCents: number;
  type: 'coin' | 'bill';
  label: string;
}
export interface MoneyConstraints {
  minTokens?: number;
  maxTokens?: number;
  requireBill?: boolean;
  forbidBill?: boolean;
  exactTokens?: number;
  /**
   * SOFT HINT ONLY - not enforced by validator
   * Indicates the question expects minimal token usage, but doesn't validate it
   * (requires a greedy algorithm solver which is not yet implemented)
   */
  preferMinimalTokens?: boolean;
}
export interface MoneyConfig {
  mode: MoneyMode;
  currency: 'EUR';
  targetCents: number;
  priceCents?: number; // For give_change mode
  paidCents?: number; // For give_change mode
  allowedDenoms: MoneyDenom[];
  constraints?: MoneyConstraints;
  options?: Array<{
    id: string;
    tokenSet: Array<{
      valueCents: number;
      count: number;
    }>;
  }>;
}
export interface MoneyFeedback {
  correct: string;
  tooLow: string;
  tooHigh: string;
  deltaHint: string;
  strategyTip: string;
}

// ============================================================
// New Question Type Definitions
// ============================================================

export interface GridShadeConfig {
  gridRows: number;
  gridCols: number;
  targetCount: number;
  mode: 'shade_count' | 'shade_fraction' | 'shade_to_match';
  preShaded?: number[];
  showCounter?: boolean;
}
export interface StepSolverStep {
  prompt: string;
  correctAnswer: string | number;
  acceptedAnswers?: (string | number)[];
  hint?: string;
  explanation?: string;
}
export interface StepSolverConfig {
  context?: string;
  steps: StepSolverStep[];
  showStepsProgressively?: boolean;
}
export interface SentenceParserConfig {
  sentence: string;
  words: string[];
  labels: string[];
  correctAssignments: Record<string, string>;
}
export interface ComparisonJudgeItem {
  id: string;
  content: string;
  label?: string;
}
export interface ComparisonJudgeConfig {
  items: ComparisonJudgeItem[];
  criterion: string;
  correctIndex: number;
}
export interface SquareRootBuilderConfig {
  targetValue: number;
  maxSize?: number;
}

/**
 * PlaceValueBuildConfig — MAB place-value composition question config.
 * The student composes a target number by adding/removing thousands/
 * hundreds/tens/ones blocks. Aligns with sister-types
 * (SquareRootBuilderConfig, BarModelPartConfig) by being namespaced under
 * data.placeValueBuild.
 */
export interface PlaceValueBuildConfig {
  /** Composition direction. `build_target` is the only supported mode for now. */
  mode?: 'build_target' | 'read_blocks';
  /** Target number the student must compose */
  target: number;
  /** Optional starting block counts (default: all zero) */
  initial?: {
    thousands?: number;
    hundreds?: number;
    tens?: number;
    ones?: number;
  };
  /** Show a running-total banner under the blocks (default true) */
  showRunningTotal?: boolean;
}

/**
 * BarModelPartConfig — Strookmodel question config.
 * The student types the value of the highlighted region of a bar that
 * represents `total` divided into `segments` equal parts.
 */
export interface BarModelPartConfig {
  /** Whole-bar value, e.g. 80 */
  total: number;
  /** Optional unit prefix shown with values (e.g. "€", "m", "kg") */
  unit?: string;
  /** Number of equal segments the bar is split into */
  segments: number;
  /** 0-based indices of highlighted segments (contiguous expected) */
  highlighted: number[];
  /** Optional override label for the whole */
  totalLabel?: string;
  /** Prompt label above the input (e.g. "Hoeveel is 25%?") */
  promptLabel?: string;
}

/**
 * StrategySolverConfig — Handig-rekenen question config.
 *
 * The student first picks a calculation strategy (splitsen, compenseren,
 * factoren, verdubbel-halveer, cijferend, ...) from a card-based picker,
 * then enters the numeric answer. The renderer enforces the two-phase flow
 * so that "Reken handig" becomes an explicit, gradable choice instead of a
 * vague instruction with a bare fill-in.
 *
 * Scoring is based on the numeric answer (binary correct/incorrect). The
 * chosen strategy is preserved in the user payload for feedback so the
 * explanation can comment on the choice (optimal / ok / suboptimal).
 */
export interface StrategySolverStrategy {
  /** Stable identifier, used in user payloads and feedback (e.g. 'splitsen'). */
  id: string;
  /** Short Dutch label shown on the card. */
  label: string;
  /** Lucide icon name. Falls back to 'Lightbulb' when omitted. */
  icon?: string;
  /** One-sentence explanation of the strategy. Shown on the card. */
  hint?: string;
  /**
   * Didactic tier of the strategy for this specific expression:
   * - 'optimal'    — the slim/handige route the seed wants to teach
   * - 'ok'         — works, acceptable
   * - 'suboptimal' — works but defeats the purpose (e.g. cijferend voor 25 × 36)
   */
  tier: 'optimal' | 'ok' | 'suboptimal';
}
export interface StrategySolverConfig {
  /** Display-only expression shown in the focus card (e.g. "25 × 36"). */
  expression: string;
  /** 3-5 strategy cards. Order is preserved (no shuffling). */
  strategies: StrategySolverStrategy[];
  /** Accepted numeric/string answers. Falls back to question.correctAnswer. */
  acceptedAnswers?: (string | number)[];
  /** Optional unit prefix/suffix shown next to the input (e.g. "€"). */
  unit?: string;
}
export interface FractionEquivalenceMatchConfig {
  pairs: Array<{
    fraction: {
      numerator: number;
      denominator: number;
      visualType?: 'pizza' | 'bar' | 'circle-clean';
    };
    equivalent: string;
  }>;
}
export interface FractionStripBuilderConfig {
  denominator: number;
  targetNumerator: number;
  mode?: 'divide' | 'shade' | 'both';
}

/**
 * DoubleNumberLineConfig — Dubbele getallenlijn question config.
 *
 * Two parallel horizontal number lines that share the same coordinate axis
 * (left edge = scale.min, right edge = scale.max on both). Known anchor
 * points link a value on the top scale to a value on the bottom scale; the
 * student drags a marker along the "ask" line to read the proportional
 * value on the other scale.
 *
 * Canonical schoolbook model (WiG / Pluspunt) for procenten van een geheel
 * and recept-opschalen (verhoudingen). Tier-band 7–14, DOK 2.
 *
 * Schema:
 *  - topScale / bottomScale: linear scales (min..max), with optional unit + label.
 *  - knownPoints: visible anchor pairs that fix the proportional mapping.
 *    Each entry's top/bottom values MUST sit at the same fraction along the
 *    shared axis — i.e. (top - topScale.min)/(topScale.max - topScale.min)
 *    === (bottom - bottomScale.min)/(bottomScale.max - bottomScale.min).
 *  - askFor: which line the student drags ('top' or 'bottom').
 *  - correctValue: numeric answer on the askFor scale.
 *  - tolerancePercent: ± tolerance as percent of the askFor scale range
 *    (default 5).
 */
export interface DoubleNumberLineConfig {
  topScale: {
    min: number;
    max: number;
    unit?: string;
    label?: string;
  };
  bottomScale: {
    min: number;
    max: number;
    unit?: string;
    label?: string;
  };
  knownPoints: Array<{
    top: number;
    bottom: number;
    label?: string;
  }>;
  askFor: 'top' | 'bottom';
  correctValue: number;
  tolerancePercent?: number;
  promptLabel?: string;
}

/**
 * CommaShiftConfig — Komma-verschuiver question config.
 *
 * Toont een kommagetal in plaatswaarde-cellen. De student schuift de komma
 * stap voor stap naar links of rechts tot het antwoord overeenkomt met de
 * gevraagde bewerking (×10, ×100, ÷10, ÷100). De cijfers blijven op hun
 * plek staan — alleen de komma beweegt. Maakt de canonieke misconceptie
 * "×10 = nul erbij" expliciet zichtbaar als komma-shift.
 *
 * Didactisch anker (Pluspunt / WiG):
 *  - ×10 = komma één plek naar rechts
 *  - ×100 = komma twee plekken naar rechts
 *  - ÷10 = komma één plek naar links
 *  - ÷100 = komma twee plekken naar links
 *  - Tier-band: 6–10 (eind groep 6 t/m eind groep 7)
 *  - DOK: 2 (toepassen van plaatswaarde-shift)
 *
 * Schema:
 *  - startValue: Nederlandse notatie, b.v. "3,45" of "12".
 *  - operation: één van '×10' | '×100' | '÷10' | '÷100'.
 *  - correctAnswer: Nederlandse notatie eindwaarde, b.v. "34,5".
 *  - acceptedAnswers: optionele extra schrijfwijzen (b.v. "34,50").
 *  - showAnimation: schakel komma-animatie aan/uit (default true).
 *
 * Validator: string-normalisatie (komma↔punt, trailing zeros gestript)
 * vergelijking tegen correctAnswer + acceptedAnswers.
 */
export interface CommaShiftConfig {
  startValue: string;
  operation: '×10' | '×100' | '÷10' | '÷100';
  correctAnswer: string;
  acceptedAnswers?: string[];
  showAnimation?: boolean;
}

/**
 * ScientificNotationBuilderConfig — Wetenschappelijke-notatie bouwer.
 *
 * De student bouwt het getal als a × 10ⁿ door (1) een coëfficiënt a te
 * typen (1 ≤ a < 10) en (2) een exponent n te kiezen (0–8). De validator
 * controleert zowel het product (a · 10ⁿ === targetValue) als de
 * canonieke vorm (1 ≤ a < 10). Schoolbook-anker: GET-WETENSCHAPPELIJK
 * en GET-WETENSCHAP-REKEN (groep 8).
 */
export interface ScientificNotationBuilderConfig {
  /** The number the student must express in scientific notation. */
  targetValue: number;
  /** Lowest exponent in the picker (default 0). */
  minExponent?: number;
  /** Highest exponent in the picker (default 8). */
  maxExponent?: number;
  /** Max decimal places allowed on the coefficient (default 1). */
  allowedDecimals?: number;
}

// ============================================================
// Spelling Pick Question Type (SPR specialist)
// ============================================================

/**
 * SpellingPickWord — one word with a single grapheme gap.
 * The rendered word = `before` + [gap tile] + `after`.
 * `correct` is the grapheme that belongs in the gap (must be one of the
 * config `graphemes`). `hint` is an optional one-liner shown under the word
 * during feedback.
 *
 * Example (ei-ij): { before: "vr", after: "", correct: "ij" } → "vrij"
 * Example (d-t):   { before: "han", after: "", correct: "d" }  → "hand"
 */
export interface SpellingPickWord {
  before: string;
  after: string;
  correct: string;
  hint?: string;
}

/**
 * SpellingPickConfig — grapheme-choice cloze for SPR recall families.
 *
 * The learner completes 1–4 words by tapping the correct grapheme tile for
 * each gap. Scoring is binary: every gap must match its `correct` grapheme.
 *
 * - variant: didactic family this item belongs to. Drives the default tile
 *   set and the framing copy. 'custom' requires an explicit `graphemes` list.
 * - graphemes: tiles offered for every word. Defaults by variant:
 *   ei-ij → ['ei','ij'], au-ou → ['au','ou'], d-t → ['d','t'].
 *   Required when variant === 'custom'.
 * - words: 1–4 words, each with a single gap.
 * - promptLabel: optional short instruction override.
 */
export interface SpellingPickConfig {
  variant: 'ei-ij' | 'au-ou' | 'd-t' | 'custom';
  graphemes?: string[];
  words: SpellingPickWord[];
  promptLabel?: string;
}

/**
 * Default grapheme tile sets per spelling_pick variant. SSOT consumed by the
 * renderer and validator so seeds may omit `graphemes` for the known families.
 */
export const SPELLING_PICK_DEFAULT_GRAPHEMES: Record<SpellingPickConfig['variant'], string[]> = {
  'ei-ij': ['ei', 'ij'],
  'au-ou': ['au', 'ou'],
  'd-t': ['d', 't'],
  custom: []
};

/**
 * Resolve the grapheme tiles for a spelling_pick config: explicit list wins,
 * otherwise fall back to the variant default. Always returns a fresh array.
 */
export function resolveSpellingPickGraphemes(config: Pick<SpellingPickConfig, 'variant' | 'graphemes'>): string[] {
  if (config.graphemes && config.graphemes.length > 0) {
    return [...config.graphemes];
  }
  return [...(SPELLING_PICK_DEFAULT_GRAPHEMES[config.variant] ?? [])];
}

// ============================================================
// Verb Ending Question Type (SWW specialist — dictation-grade)
// ============================================================

/**
 * VerbEndingItem — one verb in context with a single ENDING gap.
 * The rendered word = `before` + `stem` + [ending gap] + `after`.
 *
 * `correct` is the ending grapheme that belongs in the gap and MUST be one of
 * the resolved `endings`. An empty-string `correct` ('') means "no ending" —
 * just the bare stem (e.g. "ik werk", "ik vind").
 *
 * Example (tt-tstam): { subject: "hij", stem: "werk", correct: "t" }  → "werkt"
 * Example (tt-tstam): { subject: "ik",  stem: "werk", correct: "" }   → "werk"
 * Example (tt-tstam): { subject: "hij", stem: "vind", correct: "t" }  → "vindt"
 * Example (vt-zwak):  { subject: "hij", stem: "werk", correct: "te" } → "werkte"
 * Example (vd-kof):   { before: "ge", stem: "werk", correct: "t" }    → "gewerkt"
 */
export interface VerbEndingItem {
  /** Optional leading fragment (e.g. "ge" for a voltooid deelwoord). */
  before?: string;
  /** The verb stem shown before the gap. Required. */
  stem: string;
  /** Optional trailing fragment after the ending gap. */
  after?: string;
  /** Optional subject chip shown before the word (e.g. "hij", "ik", "wij"). */
  subject?: string;
  /** The ending that belongs in the gap. '' means "no ending" (bare stem). */
  correct: string;
  /** Optional one-liner shown during feedback. */
  hint?: string;
}

/**
 * VerbEndingConfig — werkwoord-uitgang cloze for SWW dictation families.
 *
 * The learner completes 1–4 verbs by tapping the correct ending tile for each
 * gap. Scoring is binary: every gap's chosen ending must match its `correct`.
 *
 * - variant: didactic family this item belongs to. Drives the default ending
 *   tile set and the framing copy. 'custom' requires an explicit `endings` list.
 * - endings: ending tiles offered for every verb. Defaults by variant:
 *   tt-tstam → ['', 't', 'dt'], vt-zwak → ['te', 'de', 'tte', 'dde'],
 *   vd-kof → ['t', 'd']. Required when variant === 'custom'.
 * - items: 1–4 verbs, each with a single ending gap.
 * - promptLabel: optional short instruction override.
 */
export interface VerbEndingConfig {
  variant: 'tt-tstam' | 'vt-zwak' | 'vd-kof' | 'custom';
  endings?: string[];
  items: VerbEndingItem[];
  promptLabel?: string;
}

/**
 * Default ending tile sets per verb_ending variant. SSOT consumed by the
 * renderer and validator so seeds may omit `endings` for the known families.
 * The empty string '' represents the "no ending" tile (bare stem).
 */
export const VERB_ENDING_DEFAULT_ENDINGS: Record<VerbEndingConfig['variant'], string[]> = {
  'tt-tstam': ['', 't', 'dt'],
  'vt-zwak': ['te', 'de', 'tte', 'dde'],
  'vd-kof': ['t', 'd'],
  custom: []
};

/**
 * Resolve the ending tiles for a verb_ending config: explicit list wins,
 * otherwise fall back to the variant default. Always returns a fresh array.
 */
export function resolveVerbEndings(config: Pick<VerbEndingConfig, 'variant' | 'endings'>): string[] {
  if (config.endings && config.endings.length > 0) {
    return [...config.endings];
  }
  return [...(VERB_ENDING_DEFAULT_ENDINGS[config.variant] ?? [])];
}

// ============================================================
// Word Builder Question Type (SPR specialist — full-word producer)
// ============================================================

/**
 * WordBuilderItem — one word to be produced in full from a letter bank.
 *
 * The learner reads the `clue` (which says WHICH word without revealing its
 * spelling) and taps letters from a shuffled bank to build the whole `word`.
 * There are NO options and NO grapheme choice: every letter is produced.
 *
 * `word` is the target full-word spelling. Binary scoring compares the built
 * string to this case-insensitively. `extraLetters` are optional per-item
 * decoy letters mixed into the bank so the task is recall, not rearrangement.
 *
 * Example (ei-ij): { clue: "Niet gevangen, maar ___", word: "vrij" }
 * Example (d-t):   { clue: "Je loopt op je ___ (lichaamsdeel)", word: "voet" }
 */
export interface WordBuilderItem {
  /** Short context line telling WHICH word to spell, without its spelling. */
  clue: string;
  /** The target full-word spelling. Required. */
  word: string;
  /** Optional per-item decoy letters added to the bank. */
  extraLetters?: string[];
  /** Optional one-liner shown during feedback. */
  hint?: string;
}

/**
 * WordBuilderConfig — full-word producer for SPR recall families (dictee).
 *
 * The learner produces 1–4 words in full by tapping letters from a shuffled
 * bank. Scoring is binary: every item's built string must equal its `word`
 * (case-insensitive).
 *
 * - variant: didactic family this item belongs to. Drives the default decoy
 *   letter set and the framing copy. 'custom' carries no default decoys.
 * - items: 1–4 words, each produced in full.
 * - extraLetters: config-level decoy letters added to every item's bank.
 * - promptLabel: optional short instruction override.
 */
export interface WordBuilderConfig {
  variant: 'ei-ij' | 'd-t' | 'dt' | 'custom';
  items: WordBuilderItem[];
  extraLetters?: string[];
  promptLabel?: string;
}
export interface CoordinatePlotConfig {
  gridWidth: number;
  gridHeight: number;
  startPoint?: {
    x: number;
    y: number;
    label: string;
  };
  correctPoint: {
    x: number;
    y: number;
  };
}

/**
 * Default decoy letter sets per word_builder variant. SSOT consumed by the
 * letter-bank resolver so seeds may omit decoys for the known families. These
 * are the confusable letters for each weet-family, mixed in so the bank is a
 * recall challenge rather than a pure anagram of the target word.
 */
export const WORD_BUILDER_DEFAULT_DECOYS: Record<WordBuilderConfig['variant'], string[]> = {
  'ei-ij': ['e', 'i', 'j'],
  'd-t': ['d', 't'],
  dt: ['d', 't'],
  custom: []
};

/**
 * Resolve the shuffled-input letter bank for one word_builder item.
 *
 * The bank = the target word's own letters (duplicates preserved, e.g. two
 * 's' in "kussen") PLUS decoy letters from item.extraLetters, config-level
 * extraLetters, and the variant default — but a decoy is only added when it is
 * not already needed by the word (so the bank never silently makes a wrong
 * spelling buildable that the word doesn't itself require). Returns a stable,
 * UNSHUFFLED array; the renderer shuffles deterministically by question.id +
 * item index so SSR/preview stays stable. See utils/deterministicShuffle.ts.
 */
export function resolveWordBuilderLetterBank(item: Pick<WordBuilderItem, 'word' | 'extraLetters'>, config: Pick<WordBuilderConfig, 'variant' | 'extraLetters'>): string[] {
  const wordLetters = item.word.toLowerCase().split('');
  // Count how many of each letter the word itself needs.
  const needCount = new Map<string, number>();
  for (const ch of wordLetters) {
    needCount.set(ch, (needCount.get(ch) ?? 0) + 1);
  }
  const bank = [...wordLetters];
  // Running multiplicity in the bank, so a decoy only adds beyond the word's need.
  const haveCount = new Map(needCount);
  const decoys = [...(item.extraLetters ?? []), ...(config.extraLetters ?? []), ...(WORD_BUILDER_DEFAULT_DECOYS[config.variant] ?? [])];
  for (const raw of decoys) {
    const ch = raw.toLowerCase();
    if (!ch) continue;
    // Add the decoy only if it's not already fully covered by the word's letters.
    if ((haveCount.get(ch) ?? 0) >= (needCount.get(ch) ?? 0) + 1) continue;
    bank.push(ch);
    haveCount.set(ch, (haveCount.get(ch) ?? 0) + 1);
  }
  return bank;
}

// ============================================================
// Verb Conjugation Flow Question Type
// ============================================================

export interface VerbFlowStep {
  id: string;
  prompt: string;
  type: 'choice' | 'input';
  /** For choice steps */
  options?: {
    label: string;
    value: string;
    isCorrect: boolean;
    feedback?: string;
  }[];
  /** For input steps */
  acceptedAnswers?: string[];
  hint?: string;
  /** Visual emphasis for the step */
  highlight?: string;
}
export interface VerbConjugationFlowConfig {
  verb: string;
  targetForm: string;
  steps: VerbFlowStep[];
  finalAnswer: string;
  context?: string;
}

// ============================================================
// QuestionData — Typed payload for renderer-specific content
// Replaces `Record<string, unknown>` to eliminate `as any` casts (P0-2)
// ============================================================

/** Schedule event for schedule_reading questions */
export interface ScheduleEvent {
  row: number;
  col: number;
  content: string;
}

/** Schedule config for schedule_reading questions */
export interface ScheduleConfig {
  columns: string[];
  timeSlots: string[];
  events: ScheduleEvent[];
}

/** Cell reference for schedule_reading questions */
export interface CellRef {
  row: number;
  col: number;
}

/** Matching pair for matching questions */
export interface MatchingPair {
  left: string;
  right: string;
}

/** Chart data point for bar_chart / line_chart questions */
export interface ChartDataPoint {
  label: string;
  value: number;
  [key: string]: unknown;
}

/** Pie chart slice for pie_chart questions */
export interface PieSlice {
  label: string;
  value: number;
  color?: string;
}

/**
 * Universal data payload for renderer-specific content.
 *
 * Each question type stores its config here. Known shapes are explicitly typed;
 * unknown future properties fall through to the index signature.
 */
export interface QuestionData {
  // Specialized question types (MeesterJay 4.1)
  clock?: ClockConfig;
  money?: MoneyConfig;

  // New question types
  gridShade?: GridShadeConfig;
  stepSolver?: StepSolverConfig;
  sentenceParser?: SentenceParserConfig;
  comparisonJudge?: ComparisonJudgeConfig;
  squareRootBuilder?: SquareRootBuilderConfig;
  placeValueBuild?: PlaceValueBuildConfig;
  barModelPart?: BarModelPartConfig;
  strategySolver?: StrategySolverConfig;
  fractionEquivalenceMatch?: FractionEquivalenceMatchConfig;
  fractionStripBuilder?: FractionStripBuilderConfig;
  doubleNumberLine?: DoubleNumberLineConfig;
  commaShift?: CommaShiftConfig;
  scientificNotationBuilder?: ScientificNotationBuilderConfig;
  spellingPick?: SpellingPickConfig;
  verbEnding?: VerbEndingConfig;
  wordBuilder?: WordBuilderConfig;
  coordinatePlot?: CoordinatePlotConfig;

  // Visual data types (bar_chart, line_chart, pie_chart)
  correctIndex?: number;
  labels?: string[];
  data?: ChartDataPoint[];
  slices?: PieSlice[];

  // Schedule reading
  schedule?: ScheduleConfig;
  correctCell?: CellRef;

  // Ordering
  items?: string[];
  correctOrder?: number[];

  // Matching
  pairs?: MatchingPair[];

  // Fill-in
  acceptedAnswers?: string[];
  caseSensitive?: boolean;

  // Drag-drop fill
  blanks?: Array<{
    id: string;
    correctAnswer: string;
    [key: string]: unknown;
  }>;

  // Table reading
  correctSelection?: number;

  // Number sequence
  correctValues?: (string | number)[];

  // Ratio table
  rows?: unknown[][];
  hiddenCells?: CellRef[];

  // Text highlight
  segments?: Array<{
    id: string;
    text: string;
    isCorrect?: boolean;
  }>;
  text?: string;
  correctHighlights?: Array<{
    start: number;
    end: number;
  }>;

  // Error spotting
  errors?: Array<{
    word: string;
    position: number;
    correction: string;
    rule?: string;
  }>;

  // Column arithmetic (data-first, PEDAGOGICAL_CONTRACT_SPEC §7)
  columnArithmetic?: {
    operand1: number;
    operand2: number;
    operator: '+' | '-' | '×';
  };

  // Long division (data-first, PEDAGOGICAL_CONTRACT_SPEC §7)
  longDivision?: {
    dividend: number;
    divisor: number;
  };

  // Support resolver
  representationId?: string;

  // Extensibility: unknown future properties
  [key: string]: unknown;
}

// ============================================================
// Question Core & Feedback
// ============================================================

// Base question structure
export interface QuestionCore {
  id: string;
  module: ModuleId;
  question: string;
  questionType: QuestionType;

  // CITO-specific fields (optional, for future IRT implementation)
  citoDifficulty?: number;
  citoDiscrimination?: number;

  // Skill modeling — REQUIRED for taxonomy compliance
  skill: string;
  subSkill: string;

  // Question family grouping — REQUIRED, canonical familyId
  familyId: string;

  /**
   * Universal data payload for renderer-specific content.
   * Typed via QuestionData interface — see P0-2.
   */
  data?: QuestionData;

  /**
   * Difficulty tier (1-16) for adaptive progression.
   * Maps: B6=1, B6+=2, M6=3, ..., E8+=16
   * This is the authoritative difficulty field.
   */
  tier: number;

  /**
   * Semantic concept identifier for analytics and reporting.
   * Example: "fraction_part_of_quantity"
   */
  conceptId?: string;

  /**
   * Variant number within a family+tier combination.
   * Helps prevent repetition and enables balanced selection.
   */
  variantIndex?: number;

  /**
   * FG2 Phase 2 — Equivalence-class tag for long flat-distribution families.
   *
   * Two questions sharing the same `equivalenceClass` value are treated as
   * ONE for the FG2 breadth / coverage accounting in
   * `projectMasteryToUnlock` (see `src/shared/proxyContracts/masteryProjection.ts`).
   * Mastering any single representative of a class therefore counts the
   * whole class as "touched" — collapsing the perceived path through
   * families like REK-TAFELS-BASIS (one tag per multiplication table)
   * without lowering the actual mastery bar.
   *
   * Optional and backwards-compatible: when omitted (or undefined) the
   * question forms its own singleton class keyed by its `id`, which
   * reproduces the pre-Phase-2 behaviour exactly.
   *
   * Convention: kebab-case domain-meaningful tag (e.g. `"tafel-van-7"`,
   * `"breuk-half"`, `"komma-tot-tiende"`). Scope is the family — the same
   * tag in two different families has no cross-family meaning.
   *
   * ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §FG2 — Phase 2.
   */
  equivalenceClass?: string;

  /**
   * Human-readable level code for UI and reporting.
   * Examples: "B6", "M6+", "E7", "B8+"
   */
  levelCode: string;

  /**
   * School group number (6, 7, or 8).
   */
  group: number;

  // Question content
  context?: string;
  options: string[];
  correctAnswer: number;

  // CITO anchor question flag
  isAnchor?: boolean;

  // Canonical feedback + visual aid payload
  feedback?: QuestionFeedback;
  stimulusAids?: VisualAid[];
  supportAids?: VisualAid[];

  /** Pedagogical contract metadata (PEDAGOGICAL_CONTRACT_SPEC §3) */
  pedagogy?: PedagogyContract;
}

// NEW: Structured Feedback (Stage 1)
/**
 * QuestionFeedback - Structured feedback for questions
 *
 * NOTE: This interface is used for explanation text and solution steps.
 * Visual aids are handled separately via stimulusAids/supportAids arrays.
 */
export interface QuestionFeedback {
  explanationText?: string;
  solutionSteps?: string[];
  distractorExplanations?: Record<string, string>;
}

// Full question (for practice sessions) - extends core with explanations
export interface Question extends QuestionCore {
  /**
   * VISUAL AIDS SYSTEM (CANONICAL)
   *
   * This is the WORKING system used throughout the application:
   * - stimulusAids: Shown DURING question phase (before answering)
   * - supportAids: Shown DURING feedback phase (after answering, respects showWhen)
   *
   * These arrays are the authoritative source for visual content.
   * They are defined in seed files and loaded via questionLoader.ts.
   */
  stimulusAids?: VisualAid[];
  supportAids?: VisualAid[];
  feedback?: QuestionFeedback;
}